import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRightIcon, Loader2Icon, SearchIcon, SwordsIcon, TrendingUpIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import type { OnboardingAction, OnboardingWizardFormProps } from './onboarding-wizard.interface'
import {
  type OnboardingWizardFormSchemaType,
  onboardingWizardFormSchema,
} from './onboarding-wizard.schema'

interface FieldProps {
  form: UseFormReturn<OnboardingWizardFormSchemaType>
  isLoading: boolean
}

const WebsiteUrlField: React.FC<FieldProps> = ({ form, isLoading }): ReactNode => (
  <FormField
    control={form.control}
    name="websiteUrl"
    render={({ field }) => (
      <FormItem>
        <FormLabel>What&apos;s your website? Let&apos;s check it!</FormLabel>
        <FormControl>
          <Input
            {...field}
            placeholder="example.com"
            disabled={isLoading}
            autoFocus
            className="onboarding-website-input font-bold bg-white dark:bg-background"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
)

interface ActionButtonConfig {
  action: OnboardingAction
  label: string
  icon: React.FC<{ className?: string }>
  pulseClass: string
}

const ACTION_BUTTON_LIST: ActionButtonConfig[] = [
  {
    action: 'competition',
    label: 'Review competition',
    icon: SwordsIcon,
    pulseClass: 'animate-onboarding-button-pulse',
  },
  {
    action: 'keywords',
    label: 'Find keyword opportunities',
    icon: TrendingUpIcon,
    pulseClass: 'animate-onboarding-button-pulse-delay-1',
  },
  {
    action: 'seo-state',
    label: 'Check SEO state',
    icon: SearchIcon,
    pulseClass: 'animate-onboarding-button-pulse-delay-2',
  },
]

interface ActionButtonsProps {
  isLoading: boolean
  isEnabled: boolean
  loadingAction: OnboardingAction | null
  onAction: (action: OnboardingAction) => void
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  isLoading,
  isEnabled,
  loadingAction,
  onAction,
}): ReactNode => (
  <div className="mt-auto flex flex-col gap-2">
    {ACTION_BUTTON_LIST.map(({ action, label, icon: Icon, pulseClass }) => {
      const isThisLoading = isLoading && loadingAction === action

      return (
        <Button
          key={action}
          type="button"
          variant="secondary"
          size="lg"
          disabled={!isEnabled || isLoading}
          onClick={() => onAction(action)}
          className={cn(
            'onboarding-action-button w-full cursor-pointer justify-start gap-2 transition-colors hover:bg-foreground hover:text-background',
            isEnabled && !isLoading && pulseClass
          )}
        >
          {isThisLoading ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <Icon className="size-4" />
          )}
          <span className="flex-1 text-left">{label}</span>
          <ArrowRightIcon className="size-4" />
        </Button>
      )
    })}
  </div>
)

/**
 * Onboarding form with website URL field and action buttons.
 * Uses React Hook Form with Zod validation.
 */
export const OnboardingWizardForm: React.FC<OnboardingWizardFormProps> = ({
  onSubmit,
  isLoading = false,
  defaultWebsiteUrl = '',
  className,
}): ReactNode => {
  const form = useForm<OnboardingWizardFormSchemaType>({
    resolver: zodResolver(onboardingWizardFormSchema),
    mode: 'onChange',
    defaultValues: {
      websiteUrl: defaultWebsiteUrl,
    },
  })

  const loadingActionRef = { current: null as OnboardingAction | null }

  const handleAction = async (action: OnboardingAction): Promise<void> => {
    const isValid = await form.trigger()
    if (!isValid) return
    loadingActionRef.current = action
    const data = form.getValues()
    onSubmit({ websiteUrl: data.websiteUrl, action })
  }

  return (
    <Form {...form}>
      <div className={cn('onboarding-wizard-form flex flex-col gap-5', className)}>
        <WebsiteUrlField form={form} isLoading={isLoading} />
        <ActionButtons
          isLoading={isLoading}
          isEnabled={form.formState.isValid}
          loadingAction={loadingActionRef.current}
          onAction={handleAction}
        />
      </div>
    </Form>
  )
}
